import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  AutoFixHigh as PromptIcon,
  Save as SavePromptIcon,
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';

import { nodeEditorService } from '../../services/nodeEditorService';
import { NodeEditorPrompt } from '../../types/nodeEditor.types';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  metadata?: any;
}

interface ChatPanelProps {
  chatHistory: ChatMessage[];
  onSendMessage: (message: string) => Promise<string>;
  nodeName?: string;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
  chatHistory,
  onSendMessage,
  nodeName
}) => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [prompts, setPrompts] = useState<NodeEditorPrompt[]>([]);
  const [promptMenuAnchor, setPromptMenuAnchor] = useState<null | HTMLElement>(null);
  const [showSavePromptDialog, setShowSavePromptDialog] = useState(false);
  const [newPromptName, setNewPromptName] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);
  
  // Load quick prompts
  useEffect(() => {
    loadPrompts();
  }, []);
  
  const loadPrompts = async () => {
    try {
      const quickPrompts = await nodeEditorService.getQuickPrompts();
      setPrompts(quickPrompts);
    } catch (error) {
      console.error('Failed to load prompts:', error);
    }
  };
  
  const handleSend = async () => {
    if (!input.trim() || loading) return;
    
    const message = input;
    setInput('');
    setLoading(true);
    
    try {
      await onSendMessage(message);
    } catch (error) {
      console.error('Chat error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };
  
  const handleUsePrompt = (promptText: string) => {
    setInput(promptText);
    setPromptMenuAnchor(null);
  };
  
  const handleSaveAsPrompt = async () => {
    if (!newPromptName.trim() || !input.trim()) return;
    
    try {
      await nodeEditorService.createPrompt({
        name: newPromptName,
        prompt_text: input,
        prompt_type: 'user'
      });
      setShowSavePromptDialog(false);
      setNewPromptName('');
      loadPrompts();
    } catch (error) {
      console.error('Failed to save prompt:', error);
    }
  };
  
  // Custom renderer for code blocks in markdown
  const CodeBlock = ({ node, inline, className, children, ...props }: any) => {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : 'text';
    const code = String(children).replace(/\n$/, '');
    
    if (inline) {
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
    
    return (
      <Box sx={{ position: 'relative', my: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'grey.900',
          px: 2,
          py: 0.5,
          borderRadius: '4px 4px 0 0'
        }}>
          <Typography variant="caption" color="grey.400">
            {language}
          </Typography>
          <Tooltip title="Copy code">
            <IconButton size="small" onClick={() => handleCopyCode(code)} sx={{ color: 'grey.400' }}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <SyntaxHighlighter
          language={language}
          style={vscDarkPlus}
          customStyle={{ margin: 0, borderRadius: '0 0 4px 4px' }}
          {...props}
        >
          {code}
        </SyntaxHighlighter>
      </Box>
    );
  };
  
  // Render a single message
  const renderMessage = (message: ChatMessage, index: number) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';
    
    if (isSystem) return null; // Don't show system messages
    
    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          gap: 2,
          mb: 2,
        }}
      >
        <Avatar
          sx={{
            bgcolor: isUser ? 'primary.main' : 'secondary.main',
            width: 36,
            height: 36,
          }}
        >
          {isUser ? <PersonIcon /> : <BotIcon />}
        </Avatar>
        
        <Box sx={{ maxWidth: '70%' }}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              bgcolor: isUser ? 'primary.light' : 'background.paper',
              color: isUser ? 'primary.contrastText' : 'text.primary',
              borderRadius: 2,
            }}
          >
            <ReactMarkdown
              components={{
                code: CodeBlock,
                p: ({ children }) => <Typography variant="body1">{children}</Typography>,
              }}
            >
              {message.content}
            </ReactMarkdown>
          </Paper>
          
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
            {new Date(message.timestamp).toLocaleTimeString()}
            {message.metadata?.usage && (
              <span style={{ marginLeft: 8 }}>
                ({message.metadata.usage.total_tokens} tokens)
              </span>
            )}
          </Typography>
        </Box>
      </Box>
    );
  };
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Chat with LLM</Typography>
            <Typography variant="body2" color="textSecondary">
              {nodeName ? `Editing: ${nodeName}` : 'Create a new node'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<PromptIcon />}
              onClick={(e) => setPromptMenuAnchor(e.currentTarget)}
            >
              Quick Prompts
            </Button>
            
            <Menu
              anchorEl={promptMenuAnchor}
              open={Boolean(promptMenuAnchor)}
              onClose={() => setPromptMenuAnchor(null)}
            >
              {prompts.map((prompt) => (
                <MenuItem key={prompt.id} onClick={() => handleUsePrompt(prompt.prompt_text)}>
                  <ListItemText primary={prompt.name} secondary={prompt.description} />
                </MenuItem>
              ))}
              {prompts.length === 0 && (
                <MenuItem disabled>
                  <ListItemText primary="No quick prompts available" />
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Box>
        
        {/* Quick actions */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Chip
            label="Generate frontend form"
            size="small"
            onClick={() => setInput('Generate a complete frontend form for this node using React and Material-UI')}
            clickable
          />
          <Chip
            label="Generate backend code"
            size="small"
            onClick={() => setInput('Generate the backend Python/Django code for executing this node')}
            clickable
          />
          <Chip
            label="Add validation"
            size="small"
            onClick={() => setInput('Add input validation to the node configuration')}
            clickable
          />
          <Chip
            label="Add error handling"
            size="small"
            onClick={() => setInput('Add comprehensive error handling to the node execution')}
            clickable
          />
        </Box>
      </Box>
      
      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'grey.50' }}>
        {chatHistory.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <BotIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              Start a conversation with the LLM
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Ask the AI to help you create or modify your node
            </Typography>
          </Box>
        ) : (
          <>
            {chatHistory.map((msg, idx) => renderMessage(msg, idx))}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>
      
      {/* Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type your message... (Shift+Enter for new line)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!input.trim() || loading}
              sx={{ height: 40 }}
            >
              {loading ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
            
            <Tooltip title="Save as prompt">
              <IconButton
                size="small"
                onClick={() => setShowSavePromptDialog(true)}
                disabled={!input.trim()}
              >
                <SavePromptIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          The LLM can help you generate frontend forms, backend code, add validation, and more.
        </Typography>
      </Box>
      
      {/* Save Prompt Dialog */}
      {/* This would be a separate dialog component */}
    </Box>
  );
};

export default ChatPanel;
